import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  MapPin,
  Navigation,
  User,
  Phone,
  Send,
  Truck,
  PackageCheck,
  LocateFixed,
  Search,
  Loader2,
  Crosshair,
} from "lucide-react";

import { GlobalContext } from "../../../../Context/GlobalContext";
import useCustomerDetails from "../../../Services/Customer/useCustomerDetails";

const CITY_COORDINATES = {
  Delhi: {
    lat: 28.6139,
    lng: 77.209,
  },
  Noida: {
    lat: 28.5355,
    lng: 77.391,
  },
  Gaziabad: {
    lat: 28.6692,
    lng: 77.4538,
  },
  Ghaziabad: {
    lat: 28.6692,
    lng: 77.4538,
  },
  Gurugram: {
    lat: 28.4595,
    lng: 77.0266,
  },
};

const DeliveryCard = ({ onNext, formData, setFormData }) => {
  const { latitude, longitude, userCode } = useContext(GlobalContext);
  const location = useLocation();

  const selectedCity =
    location?.state?.city || formData?.city || sessionStorage.getItem("selectedCity") || "Delhi";

  const selectedVehicle =
    location?.state?.vehicle ||
    formData?.vehicle ||
    sessionStorage.getItem("selectedVehicle") ||
    "";

  const selectedCityCenter = useMemo(() => {
    return CITY_COORDINATES[selectedCity] || CITY_COORDINATES.Delhi;
  }, [selectedCity]);

  const {
  placesAutoCompletionApi,
  placesAutoCompletionLoading,
  getFavouriteAddressApi,
  getFavouriteAddressLoading,
  getLatLongByPlaceIdApi,
  getLatLongByPlaceIdLoading,
  getPackerSlotDetailsApi,
  getPackerSlotDetailsLoading,
} = useCustomerDetails();

  const mapContainerRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const autoLocationSetRef = useRef(false);
  const activeAddressFieldRef = useRef("pickupAddress");
  const suggestionsWrapperRef = useRef(null);
  const mapIdleTimerRef = useRef(null);
  const isUserMovingMapRef = useRef(false);
  const skipNextIdleRef = useRef(true);
  const lastCenterSelectedRef = useRef("");

  const [deliveryType, setDeliveryType] = useState(
    formData?.deliveryType || "instant",
  );

  const [errors, setErrors] = useState({});
  const [activeAddressField, setActiveAddressField] = useState("pickupAddress");
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [resolvingLocation, setResolvingLocation] = useState(false);
  const [currentLocationLoading, setCurrentLocationLoading] = useState(false);
  const [isMapMoving, setIsMapMoving] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    mobile: "",
  });
const [packerSlots, setPackerSlots] = useState([]);
const [selectedScheduleDate, setSelectedScheduleDate] = useState(
  formData?.scheduleDate || "",
);

const [packerSlotGroups, setPackerSlotGroups] = useState([]);

  useEffect(() => {
    activeAddressFieldRef.current = activeAddressField;
  }, [activeAddressField]);

const [mapCenter, setMapCenter] = useState({
  lat: selectedCityCenter.lat,
  lng: selectedCityCenter.lng,
});

  useEffect(() => {
    if (formData?.deliveryType) {
      setDeliveryType(formData.deliveryType);
    }
  }, [formData?.deliveryType]);

useEffect(() => {
  const fetchPackerSlots = async () => {
    if (deliveryType !== "schedule") return;

    const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");

    const finalUserId =
      userCode ||
      sessionUser?.userCode ||
      sessionUser?.userid ||
      sessionUser?.userId ||
      "";

    if (!finalUserId) return;

    const result = await getPackerSlotDetailsApi({
      userId: finalUserId,
    });

    const groups = normalizePackerSlotGroups(result);

    setPackerSlotGroups(groups);

    const firstAvailableGroup = groups.find((group) =>
      group.slots.some((slot) => slot.isAvailable),
    );

    if (!selectedScheduleDate && firstAvailableGroup?.date) {
      setSelectedScheduleDate(firstAvailableGroup.date);

      setFormData((prev) => ({
        ...prev,
        scheduleDate: prev?.scheduleDate || firstAvailableGroup.date,
      }));
    }
  };

  fetchPackerSlots();
}, [deliveryType, userCode]);

  useEffect(() => {
  if (!selectedCity) return;

  sessionStorage.setItem("selectedCity", selectedCity);

  if (selectedVehicle) {
    sessionStorage.setItem("selectedVehicle", selectedVehicle);
  }

  setFormData((prev) => ({
    ...prev,
    city: prev?.city || selectedCity,
    vehicle: prev?.vehicle || selectedVehicle,
  }));
}, [selectedCity, selectedVehicle, setFormData]);

  useEffect(() => {
  if (!selectedCity) return;

  autoLocationSetRef.current = true;

  const cityCenter = CITY_COORDINATES[selectedCity] || CITY_COORDINATES.Delhi;

  setMapCenter(cityCenter);

  setFormData((prev) => ({
    ...prev,
    city: prev?.city || selectedCity,
    vehicle: prev?.vehicle || selectedVehicle,
  }));

  if (googleMapRef.current) {
    moveMapToCity(selectedCity);
  }
}, [selectedCity, selectedVehicle, setFormData]);

  useEffect(() => {
    loadGoogleMap();

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }

      if (mapIdleTimerRef.current) {
        clearTimeout(mapIdleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsWrapperRef.current &&
        !suggestionsWrapperRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape") {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, []);

  useEffect(() => {
    try {
      const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      const loginMobile = sessionStorage.getItem("login_mobile") || "";

      const firstName =
        sessionUser?.firstName ||
        sessionUser?.first_name ||
        formData?.firstName ||
        "";

      const lastName =
        sessionUser?.lastName ||
        sessionUser?.last_name ||
        formData?.lastName ||
        "";

      const mobile =
        sessionUser?.mobile ||
        sessionUser?.phone ||
        sessionUser?.mobileNumber ||
        sessionUser?.customerMobile ||
        loginMobile ||
        formData?.senderMobile ||
        "";

      const fullName = `${firstName} ${lastName}`.trim();

      setCustomerInfo({
        name: fullName,
        mobile,
      });

      setFormData((prev) => ({
        ...prev,
        senderName: prev?.senderName || fullName,
        senderMobile:
          prev?.senderMobile ||
          String(mobile || "")
            .replace(/\D/g, "")
            .slice(0, 10),
      }));
    } catch (error) {
      console.error("Session user parse error:", error);
    }
  }, []);

  const loadGoogleMap = () => {
    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      console.error("Google Maps API key missing");
      return;
    }

    if (window.google?.maps) {
      initMap();
      return;
    }

    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", initMap);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = initMap;

    document.body.appendChild(script);
  };

  const initMap = () => {
    if (!mapContainerRef.current || !window.google?.maps) return;
    if (googleMapRef.current) return;

    const initialCenter = selectedCityCenter;

    googleMapRef.current = new window.google.maps.Map(mapContainerRef.current, {
      center: initialCenter,
      zoom: 12,
      disableDefaultUI: true,
      zoomControl: window.innerWidth >= 768,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,

      gestureHandling: "greedy",
      draggable: true,
      scrollwheel: true,
    });

    markerRef.current = null;

    const startUserMapMove = () => {
      isUserMovingMapRef.current = true;
      setIsMapMoving(true);
    };

    googleMapRef.current.addListener("dragstart", startUserMapMove);

    googleMapRef.current.addListener("idle", () => {
      if (!googleMapRef.current) return;

      if (skipNextIdleRef.current) {
        skipNextIdleRef.current = false;
        setIsMapMoving(false);
        return;
      }

      if (!isUserMovingMapRef.current) {
        setIsMapMoving(false);
        return;
      }

      if (mapIdleTimerRef.current) {
        clearTimeout(mapIdleTimerRef.current);
      }

      mapIdleTimerRef.current = setTimeout(async () => {
        const center = googleMapRef.current.getCenter();

        if (!center) return;

        const lat = center.lat();
        const lng = center.lng();

        setIsMapMoving(false);
        isUserMovingMapRef.current = false;

        await handleMapCenterSelect(lat, lng);
      }, 600);
    });
  };

  const moveMapToCity = (cityName) => {
  if (!window.google?.maps || !googleMapRef.current) return;

  const cityCenter = CITY_COORDINATES[cityName] || CITY_COORDINATES.Delhi;

  skipNextIdleRef.current = true;

  googleMapRef.current.panTo(cityCenter);
  googleMapRef.current.setZoom(12);

  setMapCenter(cityCenter);
};

  const moveMapToLocation = (lat, lng) => {
    if (!window.google?.maps || !googleMapRef.current) {
      return;
    }

    const position = {
      lat: Number(lat),
      lng: Number(lng),
    };

    skipNextIdleRef.current = true;

    googleMapRef.current.panTo(position);
    googleMapRef.current.setZoom(16);
  };

  const reverseGeocodeLatLng = async (lat, lng) => {
    if (!window.google?.maps) {
      console.error("Google Maps not loaded");
      return null;
    }

    try {
      const geocoder = new window.google.maps.Geocoder();

      const result = await geocoder.geocode({
        location: {
          lat: Number(lat),
          lng: Number(lng),
        },
      });

      const results = result?.results || [];

      if (!results.length) {
        console.error("No address found for:", lat, lng);
        return null;
      }

      const preferredResult =
        results.find((item) =>
          item?.types?.some((type) =>
            [
              "street_address",
              "premise",
              "subpremise",
              "establishment",
              "point_of_interest",
            ].includes(type),
          ),
        ) ||
        results.find((item) => item?.geometry?.location_type === "ROOFTOP") ||
        results[0];

      return {
        address: preferredResult?.formatted_address || "",
        placeId: preferredResult?.place_id || "",
      };
    } catch (error) {
      console.error("Reverse Geocode Error:", error);
      return null;
    }
  };

  const withTimeout = (promise, timeoutMs = 3500) => {
    return Promise.race([
      promise,
      new Promise((resolve) => {
        setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  };

  const getNearbyPlaceName = async (lat, lng) => {
    if (!window.google?.maps || !googleMapRef.current) {
      return null;
    }

    try {
      const service = new window.google.maps.places.PlacesService(
        googleMapRef.current,
      );

      const location = new window.google.maps.LatLng(Number(lat), Number(lng));

      const request = {
        location,
        radius: 80,
      };

      return await withTimeout(
        new Promise((resolve) => {
          service.nearbySearch(request, (results, status) => {
            if (
              status !== window.google.maps.places.PlacesServiceStatus.OK ||
              !results?.length
            ) {
              resolve(null);
              return;
            }

            const sortedResults = results
              .filter((place) => place?.name && place?.place_id)
              .map((place) => {
                const placeLocation = place?.geometry?.location;

                const distance =
                  placeLocation && window.google?.maps?.geometry?.spherical
                    ? window.google.maps.geometry.spherical.computeDistanceBetween(
                        location,
                        placeLocation,
                      )
                    : 999999;

                return {
                  ...place,
                  distance,
                };
              })
              .sort((a, b) => a.distance - b.distance);

            const nearestPlace = sortedResults[0];

            if (!nearestPlace?.place_id) {
              resolve(null);
              return;
            }

            service.getDetails(
              {
                placeId: nearestPlace.place_id,
                fields: ["name", "formatted_address", "vicinity", "place_id"],
              },
              (placeDetails, detailsStatus) => {
                if (
                  detailsStatus ===
                    window.google.maps.places.PlacesServiceStatus.OK &&
                  placeDetails
                ) {
                  resolve({
                    name: placeDetails?.name || nearestPlace?.name || "",
                    formattedAddress:
                      placeDetails?.formatted_address ||
                      nearestPlace?.vicinity ||
                      "",
                    vicinity:
                      placeDetails?.vicinity || nearestPlace?.vicinity || "",
                    placeId:
                      placeDetails?.place_id || nearestPlace?.place_id || "",
                  });

                  return;
                }

                resolve({
                  name: nearestPlace?.name || "",
                  formattedAddress: nearestPlace?.vicinity || "",
                  vicinity: nearestPlace?.vicinity || "",
                  placeId: nearestPlace?.place_id || "",
                });
              },
            );
          });
        }),
        3500,
      );
    } catch (error) {
      console.error("Nearby Place Error:", error);
      return null;
    }
  };

  const getDisplayAddressFromLatLng = async (lat, lng) => {
    const [nearbyPlace, geocodeResult] = await Promise.all([
      getNearbyPlaceName(lat, lng),
      reverseGeocodeLatLng(lat, lng),
    ]);

    const geocodeAddress = geocodeResult?.address || "";

    if (nearbyPlace?.name) {
      const placeName = nearbyPlace.name;
      const placeAddress =
        nearbyPlace.formattedAddress ||
        nearbyPlace.vicinity ||
        geocodeAddress ||
        "";

      if (
        placeAddress &&
        !placeAddress.toLowerCase().includes(placeName.toLowerCase())
      ) {
        return `${placeName}, ${placeAddress}`;
      }

      return placeAddress || placeName;
    }

    // fallback: agar nearby place nahi mila, to geocoder ka full address use karo
    return geocodeAddress || "";
  };

  const getLatLongFromAddressText = async (addressText) => {
    if (!addressText) return null;

    const googleKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!googleKey) {
      console.error("Google Maps API key missing");
      return null;
    }

    try {
      setResolvingLocation(true);

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
          `${addressText}, India`,
        )}&key=${googleKey}`,
      );

      const data = await response.json();

      if (data?.status === "OK" && data?.results?.length > 0) {
        const selectedPlace = data.results[0];

        const lat = selectedPlace?.geometry?.location?.lat;
        const lng = selectedPlace?.geometry?.location?.lng;

        if (!lat || !lng) return null;

        return {
          address: selectedPlace?.formatted_address || addressText,
          latitude: String(lat),
          longitude: String(lng),
        };
      }

      return null;
    } catch (error) {
      console.error("Geocode Error:", error);
      return null;
    } finally {
      setResolvingLocation(false);
    }
  };

  const selectedDateGroup = useMemo(() => {
  return packerSlotGroups.find(
    (group) => group.date === selectedScheduleDate,
  );
}, [packerSlotGroups, selectedScheduleDate]);

const visibleScheduleSlots = selectedDateGroup?.slots || [];

  const extractPlaceSuggestions = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse?.predictions ||
      apiResponse?.suggestions ||
      [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.predictions)) return data.predictions;
    if (Array.isArray(data?.suggestions)) return data.suggestions;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.response)) return data.response;

    return [];
  };

  const normalizeSuggestion = (item) => {
    const address =
      item?.description ||
      item?.formatted_address ||
      item?.address ||
      item?.name ||
      item?.main_text ||
      item?.structured_formatting?.main_text ||
      item?.terms?.map((term) => term?.value).join(", ") ||
      "";

    const secondaryText =
      item?.secondary_text ||
      item?.structured_formatting?.secondary_text ||
      item?.subtitle ||
      "";

    const lat =
      item?.latitude ||
      item?.lat ||
      item?.location?.lat ||
      item?.location?.latitude ||
      item?.geometry?.location?.lat ||
      item?.geometry?.location?.latitude ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.long ||
      item?.location?.lng ||
      item?.location?.longitude ||
      item?.geometry?.location?.lng ||
      item?.geometry?.location?.longitude ||
      "";

    return {
      ...item,
      displayAddress: address,
      secondaryText,
      latitude: lat,
      longitude: lng,
      placeId: item?.place_id || item?.placeId || "",
    };
  };

  const normalizeLatLongByPlaceIdResponse = (apiResponse) => {
    const data =
      apiResponse?.response ||
      apiResponse?.data ||
      apiResponse?.result ||
      apiResponse ||
      {};

    const lat =
      data?.latitude ||
      data?.lat ||
      data?.location?.lat ||
      data?.geometry?.location?.lat ||
      "";

    const lng =
      data?.longitude ||
      data?.lng ||
      data?.long ||
      data?.location?.lng ||
      data?.geometry?.location?.lng ||
      "";

    const address =
      data?.address ||
      data?.formatted_address ||
      data?.formattedAddress ||
      data?.description ||
      data?.name ||
      "";

    return {
      latitude: lat,
      longitude: lng,
      address,
    };
  };

  const normalizeFavouriteAddress = (item) => {
    const address =
      item?.address ||
      item?.fullAddress ||
      item?.formatted_address ||
      item?.locationAddress ||
      item?.favouriteAddress ||
      "";

    const lat =
      item?.latitude ||
      item?.lat ||
      item?.location_lat ||
      item?.location?.lat ||
      "";

    const lng =
      item?.longitude ||
      item?.lng ||
      item?.location_lng ||
      item?.location?.lng ||
      "";

    return {
      ...item,
      isFavourite: true,
      displayAddress: address,
      secondaryText:
        item?.favourite || item?.tag || item?.type || "Saved address",
      latitude: lat,
      longitude: lng,
    };
  };

  const extractFavouriteAddresses = (apiResponse) => {
    const data =
      apiResponse?.response || apiResponse?.data || apiResponse?.result || [];

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.response)) return data.response;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.addresses)) return data.addresses;

    return [];
  };

  const getLatKeyName = (fieldName) => {
    return fieldName === "pickupAddress" ? "pickupLatitude" : "dropLatitude";
  };

  const getLngKeyName = (fieldName) => {
    return fieldName === "pickupAddress" ? "pickupLongitude" : "dropLongitude";
  };

  const updateAddressWithLocation = ({
    fieldName,
    address,
    lat,
    lng,
    shouldMoveMap = true,
  }) => {
    const latKey = getLatKeyName(fieldName);
    const lngKey = getLngKeyName(fieldName);

    setFormData((prev) => ({
      ...prev,
      [fieldName]: address || prev?.[fieldName] || "",
      [latKey]: String(lat || ""),
      [lngKey]: String(lng || ""),
    }));

    setMapCenter({
      lat: Number(lat),
      lng: Number(lng),
    });

    if (shouldMoveMap) {
      moveMapToLocation(lat, lng);
    }
  };

  const handleMapLocationSelect = async (lat, lng) => {
    const selectedField = activeAddressFieldRef.current || "pickupAddress";

    setResolvingLocation(true);

    try {
      const finalAddress = await getDisplayAddressFromLatLng(lat, lng);

      const safeAddress =
        finalAddress || `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;

      updateAddressWithLocation({
        fieldName: selectedField,
        address: safeAddress,
        lat,
        lng,
        shouldMoveMap: true,
      });

      setErrors((prev) => ({
        ...prev,
        [selectedField]: "",
      }));
    } catch (error) {
      console.error("Address not found for selected location:", error);
    } finally {
      setResolvingLocation(false);
    }
  };

  const handleMapCenterSelect = async (lat, lng) => {
    const selectedField = activeAddressFieldRef.current || "pickupAddress";

    const roundedLat = Number(lat).toFixed(6);
    const roundedLng = Number(lng).toFixed(6);
    const centerKey = `${selectedField}_${roundedLat}_${roundedLng}`;

    if (lastCenterSelectedRef.current === centerKey) {
      return;
    }

    lastCenterSelectedRef.current = centerKey;

    setResolvingLocation(true);

    try {
      const finalAddress = await getDisplayAddressFromLatLng(lat, lng);

      const safeAddress =
        finalAddress || `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;

      updateAddressWithLocation({
        fieldName: selectedField,
        address: safeAddress,
        lat,
        lng,
        shouldMoveMap: false,
      });

      setErrors((prev) => ({
        ...prev,
        [selectedField]: "",
      }));
    } catch (error) {
      console.error("Map center select error:", error);

      updateAddressWithLocation({
        fieldName: selectedField,
        address: `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`,
        lat,
        lng,
        shouldMoveMap: false,
      });
    } finally {
      setResolvingLocation(false);
      setIsMapMoving(false);
      isUserMovingMapRef.current = false;
    }
  };

  const handleShowRecentAddresses = async (fieldName) => {
    const finalUserCode =
      userCode ||
      JSON.parse(sessionStorage.getItem("user") || "{}")?.userCode ||
      "";

    if (!finalUserCode) return;

    const result = await getFavouriteAddressApi({
      userId: finalUserCode,
    });

    const favouriteSuggestions = extractFavouriteAddresses(result).map(
      normalizeFavouriteAddress,
    );

    if (favouriteSuggestions.length > 0) {
      setActiveAddressField(fieldName);
      activeAddressFieldRef.current = fieldName;
      setPlaceSuggestions(favouriteSuggestions);
      setShowSuggestions(true);
    }
  };

  const handleSearchPlaces = async (value) => {
    if (!value || value.trim().length < 3) {
      setPlaceSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const finalUserCode =
      userCode ||
      JSON.parse(sessionStorage.getItem("user") || "{}")?.userCode ||
      "";

    const [favouriteResult, placesResult] = await Promise.all([
      finalUserCode
        ? getFavouriteAddressApi({
            userId: finalUserCode,
          })
        : Promise.resolve(null),

     placesAutoCompletionApi({
  input: value,
  latitude: Number(mapCenter.lat || selectedCityCenter.lat || 0),
  longitude: Number(mapCenter.lng || selectedCityCenter.lng || 0),
  radius: 50000,
}),
    ]);

    const searchText = value.toLowerCase();

    const favouriteSuggestions = extractFavouriteAddresses(favouriteResult)
      .map(normalizeFavouriteAddress)
      .filter((item) =>
        item?.displayAddress?.toLowerCase().includes(searchText),
      );

    const googleSuggestions =
      extractPlaceSuggestions(placesResult).map(normalizeSuggestion);

    setPlaceSuggestions([...favouriteSuggestions, ...googleSuggestions]);
    setShowSuggestions(true);
  };

  const updateField = (name, value) => {
    let finalValue = value;

    if (name === "senderMobile" || name === "receiverMobile") {
      finalValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    if (name === "senderMobile" || name === "receiverMobile") {
      setErrors((prev) => ({
        ...prev,
        [name]:
          finalValue.length === 0
            ? "Mobile number is required"
            : finalValue.length !== 10
              ? "Mobile number must be 10 digits"
              : "",
      }));
    }
  };

  const updateRouteField = (name, value) => {
    setActiveAddressField(name);

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      [getLatKeyName(name)]: "",
      [getLngKeyName(name)]: "",
    }));

    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    searchTimerRef.current = setTimeout(() => {
      handleSearchPlaces(value);
    }, 450);
  };

  const handleSelectSuggestion = async (suggestion) => {
    const selected = suggestion?.isFavourite
      ? normalizeFavouriteAddress(suggestion)
      : normalizeSuggestion(suggestion);

    const selectedAddress =
      selected.displayAddress || formData?.[activeAddressField] || "";

    setShowSuggestions(false);
    setPlaceSuggestions([]);

    // Favourite address already has lat/lng
    if (selected.isFavourite && selected.latitude && selected.longitude) {
      updateAddressWithLocation({
        fieldName: activeAddressField,
        address: selectedAddress,
        lat: selected.latitude,
        lng: selected.longitude,
        shouldMoveMap: true,
      });

      return;
    }

    // Google suggestion: prefer placeId API
    if (selected.placeId) {
      const placeResult = await getLatLongByPlaceIdApi({
        placeId: selected.placeId,
      });

      const resolvedPlace = normalizeLatLongByPlaceIdResponse(placeResult);

      if (resolvedPlace.latitude && resolvedPlace.longitude) {
        const finalAddress = resolvedPlace.address || selectedAddress;

        updateAddressWithLocation({
          fieldName: activeAddressField,
          address: finalAddress,
          lat: resolvedPlace.latitude,
          lng: resolvedPlace.longitude,
          shouldMoveMap: true,
        });

        return;
      }
    }

    // Fallback: if suggestion already contains lat/lng
    if (selected.latitude && selected.longitude) {
      updateAddressWithLocation({
        fieldName: activeAddressField,
        address: selectedAddress,
        lat: selected.latitude,
        lng: selected.longitude,
        shouldMoveMap: true,
      });

      return;
    }

    // Final fallback: address text geocode
    const resolved = await getLatLongFromAddressText(selectedAddress);

    if (resolved) {
      updateAddressWithLocation({
        fieldName: activeAddressField,
        address: resolved.address,
        lat: resolved.latitude,
        lng: resolved.longitude,
        shouldMoveMap: true,
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [activeAddressField]: selectedAddress,
      }));
    }
  };

  const handleUseCurrentLocation = async () => {
    skipNextIdleRef.current = true;

    if (latitude && longitude) {
      await handleMapLocationSelect(latitude, longitude);
      return;
    }

    if (!navigator.geolocation) {
      console.error("Geolocation not supported");
      return;
    }

    setCurrentLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        await handleMapLocationSelect(
          position.coords.latitude,
          position.coords.longitude,
        );

        setCurrentLocationLoading(false);
      },
      (error) => {
        console.error("Use current location error:", error.message);
        setCurrentLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const handleDeliveryTypeChange = (type) => {
    setDeliveryType(type);

    setFormData((prev) => ({
      ...prev,
      deliveryType: type,
    }));
  };

const extractPackerSlotGroups = (apiResponse) => {
  const data =
    apiResponse?.data ||
    apiResponse?.response ||
    apiResponse?.result ||
    [];

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.response)) return data.response;

  return [];
};

const normalizePackerSlotGroups = (apiResponse) => {
  return extractPackerSlotGroups(apiResponse).map((group) => ({
    date: String(group?.date || ""),
    slots: Array.isArray(group?.slotDetails)
      ? group.slotDetails.map((slot) => {
          const slotTime = String(slot?.slot_time || "");
          const [startTime = "", endTime = ""] = slotTime.split("-");

const formattedSlotTime = `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`;

          return {
            slotId: String(slot?.pk_slot_id || ""),
            date: String(group?.date || ""),
            slotTime,
            startTime,
            endTime,
            label: formattedSlotTime,
            status: String(slot?.status || ""),
            isAvailable: String(slot?.status || "").toUpperCase() === "Y",
            createdOn: slot?.created_on || "",
            raw: slot,
          };
        })
      : [],
  }));
};

const formatScheduleDate = (dateValue) => {
  if (!dateValue) return "Select date";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const formatFullScheduleDate = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
};

const handleSelectScheduleSlot = (slot) => {
  if (!slot?.isAvailable) return;

  setSelectedScheduleDate(slot.date);

  setFormData((prev) => ({
    ...prev,
    scheduleDate: slot.date || "",
    scheduleTime: slot.startTime || "",
    scheduleEndTime: slot.endTime || "",
    scheduleSlotTime: slot.slotTime || "",
    scheduleSlotId: slot.slotId || "",
    scheduleSlotLabel: slot.label || "",
    packerSlotId: slot.slotId || "",
  }));

  setErrors((prev) => ({
    ...prev,
    scheduleDate: "",
    scheduleTime: "",
    scheduleSlotId: "",
  }));
};

const formatTime12Hour = (time) => {
  if (!time) return "";

  const [hours, minutes] = time.split(":");

  const date = new Date();
  date.setHours(Number(hours), Number(minutes));

  return date.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.pickupAddress?.trim()) {
      newErrors.pickupAddress = "Pickup address is required";
    }

    if (!formData?.dropAddress?.trim()) {
      newErrors.dropAddress = "Drop address is required";
    }

    if (!formData?.senderName?.trim()) {
      newErrors.senderName = "Sender name is required";
    }

    if (!formData?.receiverName?.trim()) {
      newErrors.receiverName = "Receiver name is required";
    }

    if (!formData?.senderMobile?.trim()) {
      newErrors.senderMobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.senderMobile)) {
      newErrors.senderMobile = "Enter valid 10 digit mobile number";
    }

    if (!formData?.receiverMobile?.trim()) {
      newErrors.receiverMobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(formData.receiverMobile)) {
      newErrors.receiverMobile = "Enter valid 10 digit mobile number";
    }

if (deliveryType === "schedule") {
  if (!formData?.scheduleSlotId || !formData?.scheduleDate || !formData?.scheduleTime) {
    newErrors.scheduleSlotId = "Please select a pickup slot";
  }
}

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const renderInput = ({ label, icon: Icon, placeholder, name }) => (
    <div className="group">
      <label className="mb-1 block text-[12px] font-semibold text-slate-500">
        {label}
      </label>

      <div
        className={`flex h-[44px] items-center gap-2 rounded-xl border bg-white px-3 transition-all duration-300 group-focus-within:border-blue-400 group-focus-within:ring-4 group-focus-within:ring-blue-100 ${
          errors?.[name] ? "border-red-300" : "border-slate-200"
        }`}
      >
        {Icon && <Icon size={16} className="shrink-0 text-slate-400" />}

        <input
          type={
            name === "senderMobile" || name === "receiverMobile"
              ? "tel"
              : "text"
          }
          inputMode={
            name === "senderMobile" || name === "receiverMobile"
              ? "numeric"
              : "text"
          }
          maxLength={
            name === "senderMobile" || name === "receiverMobile"
              ? 10
              : undefined
          }
          value={formData?.[name] || ""}
          onChange={(e) => updateField(name, e.target.value)}
          className="w-full bg-transparent text-[14px] text-slate-700 outline-none placeholder:text-slate-400"
          placeholder={placeholder || label}
        />
      </div>

      <div className="min-h-[10px]">
        {errors?.[name] && (
          <p className="mt-1 text-[11px] font-medium text-red-500">
            {errors[name]}
          </p>
        )}
      </div>
    </div>
  );

  const renderRouteInput = ({ title, placeholder, icon: Icon, name }) => {
    const latKey = getLatKeyName(name);
    const lngKey = getLngKeyName(name);

    return (
      <div
        ref={activeAddressField === name ? suggestionsWrapperRef : null}
        className="relative flex items-start gap-3"
      >
        <div
          className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            activeAddressField === name
              ? "bg-blue-600 text-white"
              : "bg-blue-50 text-blue-500"
          }`}
        >
          <Icon size={17} />
        </div>

        <div className="w-full">
          <div className="mb-0.5 flex items-center justify-between gap-2">
            <p className="text-[12px] font-semibold text-slate-500">{title}</p>

            {formData?.[latKey] && formData?.[lngKey] && (
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold text-green-700">
                Location set
              </span>
            )}
          </div>

          <div className="relative">
            <Search
              size={14}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={formData?.[name] || ""}
              onFocus={() => {
                setActiveAddressField(name);
                activeAddressFieldRef.current = name;

                handleShowRecentAddresses(name);
              }}
              onChange={(e) => updateRouteField(name, e.target.value)}
              placeholder={
                name === "pickupAddress" && currentLocationLoading
                  ? "Fetching current location..."
                  : placeholder
              }
              className="w-full bg-transparent py-1.5 pl-5 text-[14px] font-medium text-slate-700 outline-none placeholder:font-normal placeholder:text-slate-400"
            />

            {(placesAutoCompletionLoading ||
              getFavouriteAddressLoading ||
              getLatLongByPlaceIdLoading ||
              resolvingLocation ||
              (name === "pickupAddress" && currentLocationLoading)) &&
              activeAddressField === name && (
                <Loader2
                  size={14}
                  className="absolute right-1 top-1/2 -translate-y-1/2 animate-spin text-blue-600"
                />
              )}
          </div>

          <div className="min-h-[10px]">
            {errors?.[name] && (
              <p className="mt-1 text-[11px] font-medium text-red-500">
                {errors[name]}
              </p>
            )}
          </div>

          {name === "pickupAddress" && (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                <User size={12} className="text-blue-500" />
                <span className="font-semibold text-slate-600">
                  {formData?.senderName || customerInfo.name || "Sender"}
                </span>
              </span>

              <span className="inline-flex items-center gap-1">
                <Phone size={12} className="text-blue-500" />
                <span className="font-semibold text-slate-600">
                  {formData?.senderMobile || customerInfo.mobile || "Mobile"}
                </span>
              </span>
            </div>
          )}

          {showSuggestions &&
            activeAddressField === name &&
            placeSuggestions.length > 0 && (
              <div className="absolute left-12 right-0 top-[54px] z-50 max-h-[190px] overflow-y-auto rounded-2xl border border-slate-100 bg-white p-2 shadow-2xl [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {placeSuggestions.map((item, index) => {
                  const suggestion = item?.isFavourite
                    ? normalizeFavouriteAddress(item)
                    : normalizeSuggestion(item);

                  return (
                    <button
                      key={
                        item?.place_id ||
                        item?.placeId ||
                        item?.id ||
                        `${suggestion.displayAddress}-${index}`
                      }
                      type="button"
                      onClick={() => handleSelectSuggestion(item)}
                      className="flex w-full gap-2 rounded-xl px-3 py-2 text-left transition hover:bg-blue-50"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                        <MapPin size={15} />
                      </span>

                      <span className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className="block truncate text-[13px] font-bold text-slate-800">
                            {suggestion.displayAddress || "Unnamed location"}
                          </span>

                          {suggestion.isFavourite && (
                            <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
                              Saved
                            </span>
                          )}
                        </span>

                        {suggestion.secondaryText && (
                          <span className="mt-0.5 block truncate text-[11px] text-slate-500">
                            {suggestion.secondaryText}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
        </div>
      </div>
    );
  };

  const isSchedule = deliveryType === "schedule";
  const selectedMapAddress = formData?.[activeAddressField] || "";

  return (
    <section className="w-full px-3 pb-6 pt-8 sm:px-4 md:px-6 md:pt-12 lg:px-0 lg:pt-0">
     <div className="mx-auto mt-10 grid w-full max-w-[1020px] overflow-hidden rounded-[26px] border border-white/80 bg-white shadow-[0_22px_70px_rgba(15,23,42,0.16)] sm:mt-4 md:mt-10 lg:mt-[120px] lg:h-[800px] lg:grid-cols-[57%_43%]">
     <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
 <div className="flex-1 overflow-y-auto hide-scrollbar p-4 pb-4 sm:p-5 lg:px-7 lg:py-5">
          {/* HEADER */}
          <div className="mb-3 shrink-0">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-blue-600">
              <Truck size={13} />
              Packky Delivery
            </div>

            <h3 className="text-[22px] font-bold tracking-tight text-slate-900">
              Book Your Delivery
            </h3>

            <p className="mt-0.5 text-[13px] text-slate-500">
              Add pickup, drop-off and contact details to continue.
            </p>
          </div>

          {/* DELIVERY TYPE TOGGLE */}
          <div className="mb-3 shrink-0">
            <div className="mb-1.5 flex items-center justify-between gap-3">
              <h4 className="text-sm font-bold text-slate-800">
                Delivery Type
              </h4>

              <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-500">
                {isSchedule ? "Scheduled delivery" : "Instant delivery"}
              </span>
            </div>

            <div className="grid w-full grid-cols-2 rounded-2xl bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => handleDeliveryTypeChange("instant")}
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all duration-200 ${
                  deliveryType === "instant"
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Truck size={16} />
                Instant
              </button>

              <button
                type="button"
                onClick={() => handleDeliveryTypeChange("schedule")}
                className={`flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold transition-all duration-200 ${
                  deliveryType === "schedule"
                    ? "bg-white text-blue-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <CalendarDays size={16} />
                Schedule
              </button>
            </div>
          </div>

          {/* DATE / TIME OR INSTANT INFO  */}
         <div className="mb-2 shrink-0">
         {isSchedule ? (
  <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
    <div className="mb-2 flex items-center justify-between gap-3">
      <div>
        <h5 className="text-[13px] font-bold text-slate-800">
          Schedule pickup
        </h5>
        <p className="text-[11px] text-slate-500">
          Choose an available pickup slot.
        </p>
      </div>

      {formData?.scheduleSlotTime && (
        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-blue-700">
          {formData.scheduleSlotLabel}
        </span>
      )}
    </div>

    {getPackerSlotDetailsLoading ? (
      <div className="space-y-2">
        <div className="flex gap-2 overflow-hidden">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-8 w-24 shrink-0 animate-pulse rounded-full bg-white"
            />
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div
              key={item}
              className="h-[58px] animate-pulse rounded-xl bg-white"
            />
          ))}
        </div>
      </div>
    ) : packerSlotGroups.length > 0 ? (
      <>
     <div
  className="mb-2 overflow-x-scroll pb-1"
  style={{ WebkitOverflowScrolling: "touch" }}
>
  <div className="flex w-max gap-2">
          {packerSlotGroups.map((group) => {
            const active = selectedScheduleDate === group.date;
            const availableCount = group.slots.filter(
              (slot) => slot.isAvailable,
            ).length;

            return (
              <button
                key={group.date}
                type="button"
                onClick={() => {
                  setSelectedScheduleDate(group.date);

                  setFormData((prev) => ({
                    ...prev,
                    scheduleDate: group.date,
                    scheduleTime: "",
                    scheduleEndTime: "",
                    scheduleSlotTime: "",
                    scheduleSlotId: "",
                    scheduleSlotLabel: "",
                    packerSlotId: "",
                  }));
                }}
                className={`min-w-[110px] shrink-0 rounded-2xl border px-3 py-2 text-left transition-all ${
                  active
                    ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-100"
                    : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                }`}
              >
                <p className="text-[12px] font-bold">
                  {formatScheduleDate(group.date)}
                </p>

                <p
                  className={`mt-0.5 text-[10px] font-semibold ${
                    active ? "text-white/80" : "text-slate-400"
                  }`}
                >
                  {availableCount} slots available
                </p>
              </button>
            );
          })}
        </div>
        </div>

        <div className="mb-2 rounded-xl bg-white px-3 py-2">
          <p className="text-[11px] font-bold text-slate-700">
            {formatFullScheduleDate(selectedScheduleDate)}
          </p>
          <p className="text-[10px] text-slate-400">
            Select one time window below
          </p>
        </div>

        {visibleScheduleSlots.length > 0 ? (
         <div className="grid max-h-[150px] grid-cols-2 gap-2 overflow-y-auto hide-scrollbar pr-1 sm:grid-cols-3">
            {visibleScheduleSlots.map((slot) => {
              const isSelected =
                formData?.scheduleSlotId === slot.slotId ||
                formData?.packerSlotId === slot.slotId;

              return (
                <button
                  key={slot.slotId}
                  type="button"
                  disabled={!slot.isAvailable}
                  onClick={() => handleSelectScheduleSlot(slot)}
                  className={`rounded-xl border px-3 py-2 text-left transition-all ${
                    isSelected
                      ? "border-blue-600 bg-blue-600 text-white shadow-md shadow-blue-100"
                      : slot.isAvailable
                        ? "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50"
                        : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400 opacity-70"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Clock
                      size={13}
                      className={isSelected ? "text-white" : "text-blue-500"}
                    />

                    <span className="truncate text-[12px] font-bold">
                      {slot.label}
                    </span>
                  </div>

                  <p
                    className={`mt-1 text-[10px] font-semibold ${
                      isSelected ? "text-white/80" : "text-slate-400"
                    }`}
                  >
                    {slot.isAvailable ? "Available" : "Unavailable"}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex h-[74px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white text-center">
            <p className="text-[12px] font-medium text-slate-500">
              No slots found for this date.
            </p>
          </div>
        )}
      </>
    ) : (
      <div className="flex h-[90px] items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white text-center">
        <p className="text-[12px] font-medium text-slate-500">
          No delivery slots available right now.
        </p>
      </div>
    )}

    <div className="min-h-[14px]">
      {errors?.scheduleSlotId && (
        <p className="mt-1 text-[11px] font-medium text-red-500">
          {errors.scheduleSlotId}
        </p>
      )}
    </div>
  </div>
) : (
              <div className="flex h-full items-center gap-3 rounded-2xl border border-blue-100 bg-gradient-to-r from-blue-50 to-slate-50 px-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600 shadow-sm">
                  <Truck size={19} />
                </div>

                <div className="min-w-0">
                  <h5 className="text-sm font-bold text-slate-800">
                    Instant delivery selected
                  </h5>
                  <p className="mt-0.5 text-[12px] leading-5 text-slate-500">
                    Your driver will be assigned as soon as you confirm the
                    booking.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ROUTE */}
          <div className="mb-2 shrink-0">
            <div className="mb-1.5 flex items-center justify-between">
              <h4 className="text-sm font-bold text-slate-800">Route</h4>

              <button
                type="button"
                onClick={handleUseCurrentLocation}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-bold text-blue-700 transition hover:bg-blue-100"
              >
                <Crosshair size={12} />
                Use current
              </button>
            </div>

            <div className="relative rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-2.5">
              <div className="pointer-events-none absolute left-[35px] top-[44px] bottom-[44px] border-l-2 border-dashed border-blue-200" />

              {renderRouteInput({
                title: "From",
                placeholder: "Search pickup location",
                icon: LocateFixed,
                name: "pickupAddress",
              })}

              <div className="my-1.5 border-t border-slate-200" />

              {renderRouteInput({
                title: "To",
                placeholder: "Search drop-off location",
                icon: MapPin,
                name: "dropAddress",
              })}
            </div>
          </div>

          {/* SENDER */}
          <div className="mb-1 shrink-0">
            <h4 className="mb-1.5 text-sm font-bold text-slate-800">
              Sender Details
            </h4>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {renderInput({
                label: "Your Name",
                icon: User,
                placeholder: "Enter your name",
                name: "senderName",
              })}

              {renderInput({
                label: "Mobile Number",
                icon: Phone,
                placeholder: "Enter mobile number",
                name: "senderMobile",
              })}
            </div>
          </div>

          {/* RECEIVER */}
          <div className="mb-2 shrink-0">
            <h4 className="mb-1.5 text-sm font-bold text-slate-800">
              Receiver's Details
            </h4>

            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {renderInput({
                label: "Receiver's Name",
                icon: User,
                placeholder: "Enter receiver name",
                name: "receiverName",
              })}

              {renderInput({
                label: "Mobile Number",
                icon: Phone,
                placeholder: "Enter mobile number",
                name: "receiverMobile",
              })}
            </div>
          </div>

          {/* SUBMIT */}
          <button
            type="button"
            onClick={() => {
              if (validateForm()) {
                onNext();
              }
            }}
            className="group mt-auto flex w-full shrink-0 items-center justify-center gap-2 rounded-2xl bg-[#1f2d3f] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-slate-300 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#172334] hover:shadow-xl active:translate-y-0"
          >
            <Send
              size={17}
              className="transition group-hover:translate-x-0.5"
            />
            Driver is on the Way
          </button>
        </div>
</div>

        {/* RIGHT PANEL MAP */}
        <div className="relative order-first flex h-[420px] overflow-hidden bg-gradient-to-br from-[#eaf7ff] via-[#b9e5ff] to-[#188bdd] p-4 sm:h-[460px] md:h-[500px] lg:order-none lg:h-full lg:p-5">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 h-80 w-80 rounded-full bg-blue-300/40 blur-3xl" />

          <div className="relative z-10 flex h-full w-full flex-col gap-4">
           

            {/* MAP */}
            <div className="relative flex-1 overflow-hidden rounded-3xl border border-white/60 bg-white shadow-2xl">
              <div ref={mapContainerRef} className="h-full w-full" />

             {/* CENTER FIXED PIN */}
<div className="pointer-events-none absolute left-1/2 top-1/2 z-30 flex -translate-x-1/2 -translate-y-full flex-col items-center">
  <div
    className={`flex flex-col items-center transition-all duration-300 ease-out ${
      isMapMoving || resolvingLocation
        ? "-translate-y-3 scale-105"
        : "translate-y-0 scale-100"
    }`}
  >
    {/* Circle */}
    <div
      className={`flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-blue-600 transition-all duration-300 ${
        isMapMoving || resolvingLocation
          ? "shadow-xl"
          : "shadow-md"
      }`}
    >
      <div className="h-2 w-2 rounded-full bg-white" />
    </div>

    {/* Stem */}
    <div className="h-3 w-[2px] bg-blue-600" />
  </div>

  {/* Shadow */}
  <div
    className={`h-1.5 w-5 rounded-full bg-black/20 blur-[1px] transition-all duration-300 ease-out ${
      isMapMoving || resolvingLocation
        ? "translate-y-1 scale-75 opacity-30"
        : "translate-y-0 scale-100 opacity-50"
    }`}
  />
</div>

              {/* BOTTOM CONTROLS */}
              <div className="absolute bottom-4 left-4 right-4 hidden items-center justify-between gap-3 md:flex">
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-4 py-2 text-[12px] font-bold text-white shadow-lg transition hover:bg-blue-700"
                >
                  <Navigation size={15} />
                  Current Location
                </button>

                <div className="rounded-2xl bg-white/95 px-3 py-2 text-[11px] font-bold text-slate-600 shadow-lg backdrop-blur">
                  {resolvingLocation
                    ? "Finding exact address..."
                    : isMapMoving
                      ? "Release map to select"
                      : "Move map to choose"}
                </div>
              </div>

              {/* SELECTED ADDRESS CARD - MOBILE + DESKTOP */}
              <div className="absolute bottom-4 left-4 right-4 z-40 rounded-2xl bg-white/95 px-3 py-2 shadow-lg backdrop-blur md:bottom-16">
                <div className="flex items-center gap-2">
                  <MapPin size={15} className="shrink-0 text-blue-600" />

                  <p className="min-w-0 flex-1 truncate text-[12px] font-semibold text-slate-700">
                    {resolvingLocation
                      ? "Finding exact address..."
                      : selectedMapAddress || "Move map to choose address"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliveryCard;